/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  ApplicationView,
  HelpRequestDto,
  HelpRequestPriority,
  HelpRequestType,
  HelpRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitDialogViewService, UserContext } from '@gh/common-shared';
import { VERSION } from '@gh/config';
import { TranslationService } from '@gh/core-mls';
import { DialogRouteSnapshot, loadTinymce } from '@gh/core-ui';
import { currentDate } from '@gh/core-util';
import { timer$ } from '@gh/rx';
import { switchMap$ } from '@gh/rx/operators';
import html2canvas from 'html2canvas';
import type { Options as Html2CanvasOptions } from 'html2canvas';
import { omit } from 'lodash';
import { Observable } from 'rxjs';
import { getScreenshotName, HelpRequest } from './bug-request.model';
import { HelpRequestAttachment } from './index';
import { BuilderUserData } from '@gh/builder-shared';

const SCREENSHOT_DELAY_MS = 500;
const SCREENSHOT_FALLBACK_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

export interface HelpBugRequestDialogConfig {
  mode: HelpRequestType;
  screenshotBody: string;
  screenshotName: string;
  stacktrace?: string;
}

@Injectable()
export class HelpBugRequestBusinessService implements InitDialogViewService<HelpBugRequestDialogConfig> {
  constructor(public userContext: UserContext,
              private restService: HelpRestService,
              private dictionaryService: DictionaryService,
              private translations: TranslationService) {

  }

  initDialogView(route: DialogRouteSnapshot<HelpBugRequestDialogConfig>): any {
    return {
      request: this.generateRequest(route.input),
      priorityDict: this.dictionaryService.getByEnum(HelpRequestPriority),
      tinymce: loadTinymce(),
    };
  }

  create(helpBugRequest: HelpRequest): Observable<HelpRequest> {
    if (!helpBugRequest.attachScreenshot) {
      helpBugRequest = {
        ...helpBugRequest,
        attachments: helpBugRequest.attachments.slice(1, helpBugRequest.attachments.length),
      };
    }
    helpBugRequest = <HelpRequestDto>omit(helpBugRequest, ['attachScreenshot']);
    return this.restService.create(helpBugRequest);
  }

  private generateRequest(input: HelpBugRequestDialogConfig): Observable<HelpRequest> {
    const { mode, stacktrace } = input;
    const userData = this.userContext.data;
    const { masterZoneName, officeName } = this.getMasterOfficeNames(userData as BuilderUserData);
    const date = currentDate();

    return this.generateScreenshot().pipe(switchMap$((screenshot) => {
      const attachments = [<HelpRequestAttachment>{
        body: screenshot,
        name: getScreenshotName(date),
        contentType: 'image/png',
        encode: 'base64',
      }].concat(mode === HelpRequestType.SystemBug && stacktrace ? [{
        body: stacktrace!,
        name: 'stacktrace.json',
        contentType: 'text/json',
      }] : []);

      return [{
        mode,
        attachments,
        subject: '',
        description: this.generateRequestBody(),
        attachScreenshot: true,
        priority: mode === HelpRequestType.SystemBug ? HelpRequestPriority.Critical : HelpRequestPriority.Normal,
        buildNumber: `BE ${userData.backendBuildNumber} / FE ${VERSION}`,
        date,
        masterZoneName,
        officeName,
        login: userData.login,
      }];
    }));
  }

  private generateScreenshot(): Observable<string> {
    return timer$(SCREENSHOT_DELAY_MS).pipe(
      switchMap$(() => this.captureBodyToPngDataUrl()),
    );
  }
  
  private captureBodyToPngDataUrl(): Promise<string> {
    const base: Partial<Html2CanvasOptions> = {
      logging: false,
      useCORS: true,
      scale: 1,
    };

    const run = (foreignObjectRendering: boolean) =>
      html2canvas(document.body, { ...base, foreignObjectRendering });

    return run(true)
      .catch(() => run(false))
      .then((canvas) => canvas.toDataURL('image/png'))
      .catch((err: Error) => {
        console.warn('html2canvas failed (incl. foreignObjectRendering:false retry); using placeholder image.', err);
        return SCREENSHOT_FALLBACK_DATA_URL;
      });
  }

  private generateRequestBody(): string {
    return `
      <p><b>${this.translations.get('lbl_steps_to_reproduce')}:</b> <br><br></p>
      <p><b>${this.translations.get('lbl_actual_result')}:</b> <br><br></p>
      <p><b>${this.translations.get('lbl_expected_result')}:</b> <br><br></p>
    `;
  }

  private getMasterOfficeNames(userData: BuilderUserData): any {
    switch (userData.appView) {
      case ApplicationView.Corporate:
        return {masterZoneName: '', officeName: ''};
        break;
      case ApplicationView.Master:
        return {masterZoneName:  userData?.masterZone?.name, officeName: ''};
        break;
      case ApplicationView.Office:
        return {masterZoneName: '', officeName: userData?.office?.name};
        break;
      default:
        break;
    }
  }
}
